import React, { useEffect, useMemo, useRef, useState } from 'react'
import DatePicker from 'react-datepicker'
import { Calendar } from 'lucide-react'
import 'react-datepicker/dist/react-datepicker.css'

function toLocalDateTimeValue(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${year}-${month}-${day}T${hours}:${minutes}`
}

function parseLocalDateTimeValue(value) {
  if (!value) return null
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

export default function DateTimePicker({
  value,
  onChange,
  min,
  max,
  placeholder,
  ariaLabel,
  allowManualEntry = true,
  preventFutureSelection = false,
  hidePickerIcon = false,
}) {
  const [nowTick, setNowTick] = useState(Date.now())
  const inputRef = useRef(null)

  useEffect(() => {
    if (!preventFutureSelection) return undefined
    const timer = window.setInterval(() => setNowTick(Date.now()), 30000)
    return () => window.clearInterval(timer)
  }, [preventFutureSelection])

  const effectiveMax = useMemo(() => {
    if (max) return max
    if (!preventFutureSelection) return undefined
    return toLocalDateTimeValue(new Date(nowTick))
  }, [max, nowTick, preventFutureSelection])

  // When future selection must be blocked, use a rich calendar + time list picker
  // (react-datepicker) so future days/times are visibly greyed out and unselectable,
  // rather than only being silently rejected by the native input's `max` attribute.
  if (preventFutureSelection) {
    const selectedDate = parseLocalDateTimeValue(value)
    const maxDate = new Date(nowTick)
    // When nothing is selected yet, react-datepicker still opens on "today" by
    // default, so the time list must be restricted to <= now in that case too —
    // otherwise future times are selectable until a date is explicitly clicked.
    const isSelectedToday = selectedDate
      ? selectedDate.toDateString() === maxDate.toDateString()
      : true

    return (
      <div className="relative">
        <DatePicker
          selected={selectedDate}
          onChange={(date) => onChange(date ? toLocalDateTimeValue(date) : '')}
          showTimeSelect
          timeIntervals={1}
          timeCaption="Time"
          dateFormat="dd/MM/yyyy, h:mm aa"
          placeholderText={placeholder || 'dd/mm/yyyy, --:-- --'}
          maxDate={maxDate}
          minDate={min ? parseLocalDateTimeValue(min) : undefined}
          minTime={isSelectedToday ? new Date(new Date(nowTick).setHours(0, 0, 0, 0)) : undefined}
          maxTime={isSelectedToday ? maxDate : undefined}
          disabledKeyboardNavigation={!allowManualEntry}
          onChangeRaw={!allowManualEntry ? (e) => e.preventDefault() : undefined}
          onKeyDown={!allowManualEntry ? (e) => {
            if (e.key === 'Tab' || e.key === 'Escape') return
            e.preventDefault()
          } : undefined}
          aria-label={ariaLabel}
          autoComplete="off"
          className="w-full h-9 pl-3 pr-9 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500/30 focus:border-navy-500 dark:bg-gray-800 dark:text-gray-200 bg-white placeholder-gray-400 dark:placeholder-gray-500 transition-colors"
          wrapperClassName="w-full"
          popperPlacement="bottom-start"
          popperClassName="xtract-datepicker-popper"
          calendarClassName="xtract-datepicker-calendar"
        />
        {!hidePickerIcon && (
          <Calendar className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
        )}
      </div>
    )
  }

  const openNativePicker = () => {
    const input = inputRef.current
    if (!input || allowManualEntry) return
    if (typeof input.showPicker === 'function') {
      try {
        input.showPicker()
      } catch {
        // Some browsers gate showPicker behind trusted gestures.
      }
    }
  }

  return (
    <input
      ref={inputRef}
      type="datetime-local"
      value={value || ''}
      min={min}
      max={effectiveMax}
      placeholder={placeholder}
      aria-label={ariaLabel}
      onChange={(e) => onChange(e.target.value)}
      onClick={openNativePicker}
      onFocus={openNativePicker}
      onKeyDown={!allowManualEntry ? (e) => {
        // Keep picker interactions functional while blocking manual text edits.
        if (e.key === 'Tab' || e.key === 'Escape' || e.key.startsWith('Arrow')) return
        e.preventDefault()
      } : undefined}
      onPaste={!allowManualEntry ? (e) => e.preventDefault() : undefined}
      onDrop={!allowManualEntry ? (e) => e.preventDefault() : undefined}
      className={`w-full h-9 px-3 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500/30 focus:border-navy-500 dark:bg-gray-800 dark:text-gray-200 bg-white transition-colors ${hidePickerIcon ? 'datetime-picker-no-icon' : ''}`}
    />
  )
}
